// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * Product telemetry's closed catalog (ADR-0081, amendment 2026-10-06 "Trial telemetry"): the onboarding funnel,
 * the areas a person can look at, the settings they can change, the codes of the messages they can see, and every
 * named event with its category, the code words after its colon and the attributes it may carry.
 *
 * Phone Home keeps the same lists (`capturepoint-v6-telemetry-catalog.ts`), so it can say "never visited" and put
 * the funnel in order. The full list with what each event answers is docs/architecture/telemetry-catalog.md.
 *
 * Everything here is a code word or a number. An event outside the catalog, a code word not listed for it, or an
 * attribute it does not declare is dropped on the computer (`telemetryCatalogEvent`), so no name the person typed
 * can ride along.
 */
import type { TelemetryCategory, TelemetrySeverity } from './telemetry';

/**
 * When a person counts as stuck (owner, 2026-10-06). One place, so they are easy to change:
 * - `noProgressMs`: active on one area, or waiting on one setup stage, this long with nothing moving on.
 * - `inactiveDays`: no session for this many days, after which the last step reached is where they stopped
 *   (Phone Home's reports use the same number).
 */
export const telemetryStuckThresholds = { noProgressMs: 3 * 60_000, inactiveDays: 3 } as const;

/**
 * The onboarding funnel, in order, each recorded once per installation with the time since first launch. Owner
 * decision 2026-10-06: these replace the earlier ten "first time anything, on any job" steps. The tour is counted
 * as the tour, and only the person's own jobs reach the own_* steps; the tour's Confirm and Export never count as
 * their first review or export.
 */
export const telemetryFunnelSteps = [
  'first_launch',
  'welcome',
  'tour_started',
  'tour_finished',
  'own_job_created',
  'own_source_chosen',
  'own_setup_start',
  'own_setup_ready',
  'own_first_review',
  'own_10_reviewed',
  'own_export_set_up',
  'own_first_export',
  'own_second_batch',
  'own_training',
  'checkout_started',
  'license_active',
] as const;
export type TelemetryFunnelStep = (typeof telemetryFunnelSteps)[number];

/** Which kind of job an event happened in (attr `job`). Job ids and names never leave the computer. */
export const telemetryJobKinds = ['tour', 'sample', 'own'] as const;
export type TelemetryJobKind = (typeof telemetryJobKinds)[number];

/**
 * What a session starts with, added to `session.start` beside `trial_day` while it waits to be sent: `license_state`
 * (licensing's runtime state, a code word), `own_jobs` and `sample_jobs` (counts; the tour's job is a sample job), and
 * from the main window `tour` and `hints` (`TelemetrySessionContext` below).
 */
export const telemetrySessionTour = ['none', 'running', 'left', 'finished'] as const;
export type TelemetrySessionContext = {
  license_state?: string;
  own_jobs?: number;
  sample_jobs?: number;
  tour?: (typeof telemetrySessionTour)[number];
  hints?: 'on' | 'off';
};

/**
 * Every area a person can look at (screen event names), from renderer/telemetry-area.ts. Main records only these.
 * - `tour`: any screen of the job a tour is running on (the tour is a card over the real screens, so its time is
 *   the tour's, not the person's own use of those screens); `tour.step` says where in the tour.
 * - `job/start`: a job with nothing in it yet (where its pages come from); `job/reading`: pages being read with no
 *   document to show yet; `job/caught-up`: every document has left (All caught up).
 * - `job/documents/<tab>`: what the documents list asks about: `calibration` (review work and
 *   teaching examples, while the job calibrates) or `review`. An open document is not an area of its own: the list and the
 *   document it opened are one screen, side by side.
 * - `job/setup/wait`: a first setup reading its documents, its failure, and its hand-off to the job.
 * - `job/setup/export/naming` and `/content-central`: the File naming and Content Central windows over Export.
 */
export const telemetryAreas = [
  'welcome',
  'jobs',
  'tour',
  'cluster',
  'job/start',
  'job/reading',
  'job/caught-up',
  'job/documents/review',
  'job/documents/calibration',
  'job/setup/wait',
  'job/setup/document-types',
  'job/setup/document-starts',
  'job/setup/thresholds',
  'job/setup/lookups',
  'job/setup/watched-folder',
  'job/setup/learning/teach',
  'job/setup/learning/train',
  'job/setup/learning/results',
  'job/setup/export/when',
  'job/setup/export/files',
  'job/setup/export/destination',
  'job/setup/export/backup',
  'job/setup/export/elsewhere',
  'job/setup/export/exceptions',
  'job/setup/export/activity',
  'job/setup/export/naming',
  'job/setup/export/content-central',
  'job/break-sheets',
  'learning/teach',
  'learning/train',
  'learning/results',
  'settings/overview',
  'settings/jobs',
  'settings/cluster',
  'settings/license',
  'settings/performance',
  'settings/storage',
  'settings/connections',
  'settings/appearance',
  'settings/updates',
  'settings/support',
] as const;
export type TelemetryArea = (typeof telemetryAreas)[number];

/** Every setting whose change is recorded (`setting.changed:<id>`). */
export const telemetrySettings = [
  'theme',
  'accent',
  'tooltips',
  'zoom',
  'processing-mode',
  'storage-location',
  'update-channel',
  'support-access',
  'tips-consent',
  'training-schedule',
  'show-hints',
  'pilot-mode',
  'review-thresholds',
  'document-types',
  'document-starts',
  'export-trigger',
  'export-naming',
  'export-retention',
  'export-destination',
  'export-archive-pdf',
  'export-backup-folder',
  'export-files',
  'export-exceptions-folder',
  'lookups',
  'reset-learning',
  'content-central-connection',
  'calibration',
  'graphics-cards',
] as const;
export type TelemetrySetting = (typeof telemetrySettings)[number];

/**
 * The codes of the failures and refusals a person can see (`ui.error:<code>`), one per thing they could not do, never
 * per sentence. The first block is the trial's path; the rest, by area, every other banner and toast that is not a
 * plain success or notice. `other`: a documents-screen message whose code was not given (none today; a spare).
 * docs/architecture/telemetry-catalog.md lists what each one means; tests/telemetry-tests/ui-error-codes.cjs keeps this
 * list and the screens in step.
 */
export const telemetryUiErrorCodes = [
  'setup-failed',
  'setup-resume-failed',
  'tour-samples-not-fed',
  'tour-could-not-start',
  'source-empty-folder',
  'source-unsupported-files',
  'source-no-pages',
  'source-no-scanner',
  'scanner-fault',
  'job-name-taken',
  'job-create-failed',
  'work-refused',
  'confirm-no-type',
  'confirm-not-read',
  'confirm-line-amount',
  'confirm-field-required',
  'confirm-still-reading',
  'confirm-failed',
  'export-folder-not-prepared',
  'export-not-turned-on',
  'export-date-format',
  'export-save-failed',
  'export-failed',
  'content-central-failed',
  'speed-test-refused',
  'speed-test-failed',
  'training-refused',
  'checkout-failed',
  'extend-failed',
  'more-time-failed',
  'invoice-failed',
  'activation-failed',
  'partner-code-failed',
  'contact-details-needed',
  'other',
  // Jobs
  'job-list-load-failed',
  'job-rename-failed',
  'job-delete-failed',
  'job-import-failed',
  'job-setup-download-failed',
  'job-save-failed',
  'job-refresh-failed',
  'sample-job-failed',
  // Job setup
  'setup-interrupted',
  'setup-stop-failed',
  'setup-save-failed',
  'setup-save-blocked',
  'setup-read-more-failed',
  'sample-move-failed',
  'field-name-taken',
  'structure-check-failed',
  'type-remove-refused',
  'type-remove-failed',
  'reset-learning-refused',
  'reset-learning-failed',
  'lookup-load-failed',
  'lookup-import-failed',
  'lookup-save-failed',
  'lookup-check-failed',
  'lookup-busy',
  // Export setup, Content Central and other destinations
  'export-setup-load-failed',
  'export-save-refused',
  'export-run-refused',
  'export-documents-failed',
  'export-folder-choose-failed',
  'export-folder-open-failed',
  'export-activity-load-failed',
  'export-recovery-failed',
  'filing-examples-failed',
  'naming-invalid',
  'connections-load-failed',
  'connection-remove-failed',
  'catalog-load-failed',
  'catalog-plan-failed',
  'catalog-plan-blocked',
  'catalog-matching-failed',
  'catalog-setup-failed',
  'content-central-types-changed',
  'content-central-paused',
  'cloud-destination-load-failed',
  'cloud-destination-failed',
  'cloud-sign-in-failed',
  'cloud-test-failed',
  'cloud-delivery-unconfirmed',
  // Documents and review
  'engine-not-ready',
  'engine-restarting',
  'engine-not-responding',
  'pages-add-failed',
  'pages-not-read',
  'pages-recover-failed',
  'queue-change-failed',
  'processing-status-failed',
  'processing-pause-failed',
  'documents-load-failed',
  'document-open-failed',
  'document-gone',
  'page-preview-failed',
  'panel-layout-failed',
  'search-failed',
  'fields-not-read',
  'confirm-busy',
  'confirm-unsaved-values',
  'confirm-changed',
  'field-edit-refused',
  'field-save-busy',
  'field-save-failed',
  'field-add-failed',
  'field-gone',
  'text-select-refused',
  'line-item-busy',
  'line-item-save-failed',
  'table-find-busy',
  'table-find-failed',
  'table-not-found',
  'page-remove-busy',
  'page-remove-failed',
  'page-restore-failed',
  'page-turn-busy',
  'page-turn-failed',
  'document-delete-busy',
  'document-delete-failed',
  'document-restore-failed',
  'deleted-load-failed',
  'boundary-refused',
  'boundary-save-failed',
  'boundary-undo-failed',
  'type-change-busy',
  'type-change-failed',
  'recheck-busy',
  'recheck-failed',
  'reread-busy',
  'reread-failed',
  'job-fields-apply-failed',
  'improvement-busy',
  'improvement-failed',
  'improvement-undo-failed',
  'documents-return-failed',
  'detached-change-failed',
  'close-refused',
  // Learning
  'learning-load-failed',
  'training-stop-failed',
  'training-schedule-invalid',
  'suggestion-dismiss-failed',
  'suggestion-settings-failed',
  // Settings and this computer
  'appearance-load-failed',
  'appearance-save-failed',
  'processing-mode-load-failed',
  'processing-mode-save-failed',
  'graphics-cards-load-failed',
  'graphics-cards-save-failed',
  'computer-check-failed',
  'speed-boost-fault',
  'speed-test-load-failed',
  'job-speed-load-failed',
  'job-speed-failed',
  'storage-load-failed',
  'storage-move-failed',
  'update-channel-load-failed',
  'update-channel-save-failed',
  'update-install-refused',
  'support-access-load-failed',
  'support-access-save-failed',
  'cluster-load-failed',
  'cluster-start-failed',
  'cluster-action-failed',
  'cluster-copy-failed',
  // License and tips
  'license-load-failed',
  'license-check-failed',
  'license-support-needed',
  'tips-consent-failed',
  'tips-sign-up-failed',
  // The email asks (shared/email-asks.ts)
  'email-ask-failed',
] as const;
export type TelemetryUiErrorCode = (typeof telemetryUiErrorCodes)[number];

/** An attribute an event may carry: a number, a yes/no, or one of a fixed list of code words. */
export type TelemetryAttrSpec = 'number' | 'boolean' | readonly string[];

export type TelemetryEventSpec = {
  readonly category: TelemetryCategory;
  /** The code words after the colon. Absent: the event's name is the family alone. */
  readonly suffixes?: readonly string[];
  readonly attrs?: Readonly<Record<string, TelemetryAttrSpec>>;
  /** Who may record it: a window (validated in main) or main itself. */
  readonly from: 'renderer' | 'main' | 'both';
  readonly severity?: TelemetrySeverity;
  /** Also reach this funnel step; with `ownOnly`, only when the event's job is the person's own. */
  readonly funnel?: TelemetryFunnelStep;
  readonly ownOnly?: boolean;
  /** Recorded at most once per session for each name (a hint shown, a blocked button shown). */
  readonly oncePerSession?: boolean;
  /** `false`: each occurrence is its own event, because its attributes matter (repeats otherwise fold into a count). */
  readonly fold?: false;
};

const tourSteps = [
  'pane',
  'starts',
  'add',
  'read',
  'confirm',
  'review',
  'export',
  'files',
  'turn',
] as const;
const hints = ['first-review', 'computer-check', 'assisted', 'let-it-run'] as const;
/** Every setup-screen tip (renderer/setup-tips.ts SETUP_TIPS ids; tests/telemetry-tests/tour-hints.cjs keeps them in step). */
export const telemetryTips = [
  'no-templates',
  'asks-more',
  'private',
  'clears-many',
  'thresholds',
  'click-field',
  'math',
  'right-click',
  'lookups',
  'new-layout',
  'speed-boost',
  'separators',
  'naming',
  'print-separators',
  'export',
  'fix-teaches',
  'windows',
  'barcode-starts',
  'smarter',
  'focus',
  'sheets-with-values',
  'ctrl-enter',
  'autopilot',
  'search',
  'page-count',
  'split',
  'clean-pages',
  'not-sure',
  'values-travel',
  'undo',
  'schedule',
  'enter',
  'formats',
  'f1',
  'reorder',
  'backup',
  'teach',
  'barcode-fields',
  'computer-check',
  'history',
  'aiq',
  'ai-review',
  'pause',
  'carry',
  'legacy',
  'appearance',
  'early',
] as const;
export type TelemetryTip = (typeof telemetryTips)[number];
const tips = telemetryTips;
const setupStages = ['reading', 'separating', 'types', 'fields', 'values', 'files'] as const;
const exportKinds = ['folder', 'content-central', 'cloud', 'none'] as const;
const exportTriggers = ['manual', 'continuous', 'scheduled'] as const;
// Two pilot modes since 2026-10-19 (Assisted became Autopilot).
const pilotModes = ['manual', 'autopilot'] as const;
const plans = ['monthly', 'yearly'] as const;
const fileBuckets = ['0', '1', '2-10', '11-100', '100+'] as const;
const speedTestBlockers = [
  'already-running',
  'no-sample-documents',
  'capture-in-progress',
  'captures-waiting',
  'captures-failed',
  'recently-active',
  'license',
] as const;
/** The email asks (shared/email-asks.ts emailAskKinds); `extra-week` is the tips line on Add 7 free days. */
const emailAsks = ['learning-note', 'speed-report', 'shortcut-sheet'] as const;
const settingKinds = ['on', 'off', 'chosen', 'up', 'down', 'reset', 'added', 'removed'] as const;
/**
 * Product-defined values a setting may be changed to (`choice`); never typed text. Theme; pilot mode; update channel;
 * Processing mode (auto, faster, higher-accuracy); how documents start (the job's separation mode); export
 * (trigger, destination kind, a folder export's files); tips consent skipped; and the accent swatches by name (`custom`:
 * a color not a swatch).
 */
export const telemetrySettingChoices = [
  'light',
  'dark',
  'manual',
  'autopilot',
  'stable',
  'beta',
  'auto',
  'faster',
  'higher-accuracy',
  'automatic',
  'existing-barcode',
  'automatic-barcode',
  'capturepoint-separator',
  'file-per-document',
  'continuous',
  'scheduled',
  'folder',
  'content-central',
  'cloud',
  'skipped',
  'pdf-only',
  'pdf-data-text',
  'ocean',
  'black',
  'orange',
  'teal',
  'berry',
  'sky',
  'ice',
  'white',
  'yellow',
  'pink',
  'custom',
] as const;
export type TelemetrySettingChoice = (typeof telemetrySettingChoices)[number];
const settingChoices = telemetrySettingChoices;
/**
 * Named windows (`dialog.opened:<name>`): each `WorkspaceDialog` names itself (its `telemetry` prop), and a few other
 * windows carry `data-telemetry-dialog`. A window not listed is counted as `dialog:<its class>`. `none` is not a
 * window for telemetry: a screen shown in a window (Settings, Learning, Scan jobs: counted as its area), an always-present
 * popover, the selected-text menu.
 */
export const telemetryDialogs = [
  'naming',
  'naming-discard',
  'naming-close',
  'content-central',
  'content-central-create',
  'connections',
  'break-sheets',
  'sample-choice',
  'new-job',
  'new-job-connections',
  'lookup',
  'lookup-leave',
  'job-setup',
  'setup-wait',
  'setup-sample',
  'setup-discard',
  'setup-save-prompt',
  'stop-setup',
  'help-reading-sources',
  'help-value-types',
  'help-default-value',
  'help-required',
  'help-looks-like',
  'help-review',
  'help-learning',
  'export-results',
  'confirm-action',
  'corrections',
  'type-preview',
  'field-source',
  'job-import',
  'aiq',
  'more-tips',
  'score-guide',
  'rename-job',
  'reset-learning',
  'shortcuts',
  'welcome-samples',
  'link-job',
  'deleted',
] as const;

/**
 * Every named event. The key is the family: the event's name is `family` or `family:<suffix>`. Attributes main adds
 * itself to every catalog event (`job`, `job_n`, `trial_day`, `area`) are not listed per event.
 */
export const telemetryEventCatalog = {
  // ---- The tour (§5.4). ----
  // A start and a finish are each their own event (a second tour in the same batch must keep its `again` and `ms`).
  'tour.start': {
    category: 'feature',
    from: 'renderer',
    funnel: 'tour_started',
    fold: false,
    suffixes: ['welcome', 'new-job', 'settings', 'link', 'own-offer', 'hint', 'other'],
    attrs: { again: 'boolean' },
  },
  'tour.ready': {
    category: 'perf',
    from: 'renderer',
    attrs: {
      read_again: 'number',
      reader_problem: ['none', 'field-reader-missing', 'field-reader-broken'],
      reader_download_ms: 'number',
    },
  },
  'tour.step': {
    category: 'feature',
    from: 'renderer',
    suffixes: tourSteps,
    attrs: {
      n: 'number',
      own: 'boolean',
      via: ['next', 'control', 'auto', 'back'],
      ms_prev_step: 'number',
    },
  },
  'tour.left': {
    category: 'feature',
    from: 'renderer',
    fold: false,
    suffixes: [...tourSteps, 'reading'],
    attrs: { own: 'boolean', ms_in_tour: 'number', at: ['reading', 'card'] },
  },
  'tour.finished': {
    category: 'feature',
    from: 'renderer',
    funnel: 'tour_finished',
    fold: false,
    attrs: { own: 'boolean', ms: 'number' },
  },
  'tour.offer': { category: 'feature', from: 'renderer', suffixes: ['shown', 'yes', 'no'] },
  // ---- Hints and tips. ----
  'hint.shown': { category: 'feature', from: 'renderer', suffixes: hints, oncePerSession: true },
  'hint.closed': {
    category: 'feature',
    from: 'renderer',
    suffixes: hints,
    attrs: { how: ['x', 'rule'] },
  },
  'hint.action': {
    category: 'feature',
    from: 'renderer',
    suffixes: hints,
    attrs: { action: ['speed-test', 'pilot-mode', 'review', 'other'] },
  },
  'tip.shown': { category: 'feature', from: 'renderer', suffixes: tips, oncePerSession: true },
  'tip.opened': { category: 'feature', from: 'renderer', suffixes: tips },
  // ---- Starting a job (§5.5). ----
  'job.start-choice': {
    category: 'feature',
    from: 'renderer',
    suffixes: [
      'own-documents',
      'content-central',
      'saved-job',
      'earlier-capturepoint',
      'manual',
      'tour',
    ],
  },
  'job.new.closed': {
    category: 'feature',
    from: 'renderer',
    suffixes: ['start-choice', 'name', 'source', 'other'],
  },
  'job.created': {
    category: 'feature',
    from: 'main',
    funnel: 'own_job_created',
    ownOnly: true,
    fold: false,
    suffixes: telemetryJobKinds,
    attrs: {
      start: [
        'new',
        'manual',
        'content-central',
        'saved-job',
        'earlier-capturepoint',
        'sample',
        'tour',
      ],
    },
  },
  'source.chosen': {
    category: 'feature',
    from: 'renderer',
    funnel: 'own_source_chosen',
    ownOnly: true,
    suffixes: ['files', 'folder', 'scanner', 'simulator', 'content-central'],
  },
  'source.outcome': {
    category: 'feature',
    from: 'both',
    suffixes: [
      'picked',
      'cancelled',
      'empty-folder',
      'unsupported-files',
      'no-scanner',
      'no-pages',
    ],
    attrs: { files: fileBuckets },
  },
  'scanner.fault': {
    category: 'error',
    from: 'renderer',
    severity: 'med',
    suffixes: [
      'feeder-empty',
      'jam',
      'double-feed',
      'cover-open',
      'off',
      'in-use',
      'did-not-start',
      'driver-stopped',
      'page-not-saved',
      'twain-missing',
      'busy',
      'other',
    ],
    attrs: { arch: ['x64', 'x86', 'unknown'] },
  },
  'setup.sample-choice': {
    category: 'feature',
    from: 'renderer',
    suffixes: ['start-sooner', 'learn-from-everything', 'closed'],
  },
  'work.refused': {
    category: 'error',
    from: 'main',
    severity: 'med',
    suffixes: [
      'trial-expired',
      'license-expired',
      'suspended',
      'deactivated',
      'clock-check-required',
      'not-recognised',
      'other',
    ],
    attrs: {
      work: [
        'acquisition-session',
        'source-file',
        'ocr',
        'document-understanding',
        'reprocessing',
        'retry',
        'cluster',
        'learning',
        'new-batch',
        'speed-test',
        'other',
      ],
    },
  },
  // ---- Reading and setup (§5.6). ----
  'setup.start': {
    category: 'feature',
    from: 'main',
    funnel: 'own_setup_start',
    ownOnly: true,
    suffixes: ['files', 'folder', 'scanner', 'simulator', 'resume', 'content-central', 'other'],
  },
  'setup.stage': {
    category: 'perf',
    from: 'renderer',
    suffixes: setupStages,
    attrs: { ms: 'number', pages: 'number' },
  },
  'setup.stopped': {
    category: 'feature',
    from: 'both',
    fold: false,
    attrs: { stage: [...setupStages, 'unknown'], ms: 'number' },
  },
  'setup.failed': {
    category: 'error',
    from: 'renderer',
    severity: 'high',
    suffixes: ['ocr', 'memory', 'engine', 'unsupported', 'other'],
    attrs: { stage: [...setupStages, 'unknown'] },
  },
  'setup.ready': {
    category: 'perf',
    from: 'both',
    funnel: 'own_setup_ready',
    ownOnly: true,
    attrs: { ms_since_created: 'number', documents: fileBuckets, types_found: 'number' },
  },
  'setup.handoff': {
    category: 'feature',
    from: 'renderer',
    suffixes: ['start-checking', 'stayed', 'auto', 'closed'],
    attrs: { ms: 'number' },
  },
  'reading.no-progress': {
    category: 'stuck',
    from: 'renderer',
    attrs: { stage: [...setupStages, 'importing', 'unknown'], minutes: 'number' },
  },
  'reader.problem': {
    category: 'error',
    from: 'renderer',
    severity: 'high',
    suffixes: ['field-reader-missing', 'field-reader-broken'],
  },
  'engine.outage-shown': {
    category: 'error',
    from: 'renderer',
    severity: 'med',
    oncePerSession: true,
  },
  'engine.patience-spent': {
    category: 'error',
    from: 'renderer',
    severity: 'high',
    oncePerSession: true,
  },
  'queue.stalled': {
    category: 'error',
    from: 'renderer',
    severity: 'high',
    attrs: { files: fileBuckets },
  },
  'component.wait': {
    category: 'perf',
    from: 'main',
    attrs: { pack: ['ocr', 'reader', 'layout', 'filing-advisor', 'sample-reader', 'other'] },
  },
  'provision.choice': {
    category: 'feature',
    from: 'main',
    suffixes: ['retry', 'quit'],
    attrs: { attempt: 'number', ms: 'number' },
  },
  // ---- Needs review (§5.7). ----
  'review.summary': {
    category: 'perf',
    from: 'renderer',
    attrs: {
      opened: 'number',
      confirmed: 'number',
      confirmed_unchanged: 'number',
      confirmed_changed: 'number',
      fields_changed: 'number',
      type_changed: 'number',
      skipped: 'number',
      back: 'number',
      splits: 'number',
      joins: 'number',
      pages_removed: 'number',
      lines_ok: 'number',
      refused: 'number',
      active_ms: 'number',
      median_ms_per_confirm: 'number',
      waiting_start: 'number',
      waiting_end: 'number',
    },
  },
  'review.confirm-refused': {
    category: 'error',
    from: 'renderer',
    severity: 'low',
    suffixes: ['no-type', 'not-read', 'line-amount', 'field-required', 'still-reading', 'other'],
  },
  'review.caught-up': { category: 'feature', from: 'renderer', oncePerSession: true },
  'review.long-document': { category: 'stuck', from: 'renderer', attrs: { minutes: 'number' } },
  // Turn page: each turn that was saved (rare, and its three numbers are the answer). `count`: turns saved in this
  // window so far; `quarter_turns`: where the page was left, in quarter turns from how it was scanned (0 to 3);
  // `guessed`: the document was held because the page's direction was a guess.
  'review.page-turned': {
    category: 'feature',
    from: 'renderer',
    fold: false,
    attrs: { count: 'number', quarter_turns: 'number', guessed: 'boolean' },
  },
  // ---- Learning (§5.8). ----
  'training.start': {
    category: 'feature',
    from: 'main',
    funnel: 'own_training',
    ownOnly: true,
    suffixes: ['manual', 'scheduled'],
  },
  // Refused: main where a start is refused (the workstation guard, the license); a window where Start training is shown unavailable.
  'training.refused': {
    category: 'error',
    from: 'both',
    severity: 'low',
    suffixes: ['not-eligible', 'battery', 'busy', 'memory', 'license', 'other'],
  },
  'training.finished': {
    category: 'feature',
    from: 'main',
    suffixes: ['adopted', 'kept', 'failed'],
    attrs: { changes_adopted: 'number', changes_refused: 'number', mode: ['manual', 'scheduled'] },
  },
  // ---- Export and pilot mode (§5.9). ----
  // Each save is kept (it is rare, and its trigger and kind are the answer).
  'export.saved': {
    category: 'feature',
    from: 'main',
    fold: false,
    funnel: 'own_export_set_up',
    ownOnly: true,
    attrs: { trigger: exportTriggers, kind: exportKinds },
  },
  'export.save-failed': {
    category: 'error',
    from: 'renderer',
    severity: 'high',
    suffixes: ['folder-not-prepared', 'not-turned-on', 'date-format', 'other'],
    attrs: { kind: exportKinds },
  },
  'export.blocked': {
    category: 'feature',
    from: 'renderer',
    oncePerSession: true,
    suffixes: [
      'choose-folder',
      'finish-content-central',
      'retry-examples',
      'still-checking',
      'save-first',
      'paused',
      'job-not-ready',
      'other',
    ],
  },
  'export.done': {
    category: 'feature',
    from: 'main',
    funnel: 'own_first_export',
    ownOnly: true,
    suffixes: ['manual', 'automatic'],
    attrs: { documents: fileBuckets },
  },
  'content-central.failed': {
    category: 'error',
    from: 'both',
    severity: 'high',
    suffixes: [
      'unreachable',
      'certificate',
      'key-refused',
      'wrong-key-type',
      'timeout',
      'invalid-address',
      'other',
    ],
    attrs: { action: ['connect', 'check', 'replace-key'] },
  },
  // ---- Batches. ----
  'batch.added': {
    category: 'feature',
    from: 'main',
    fold: false,
    attrs: { n: 'number', pages: fileBuckets },
  },
  // ---- Speed test (§5.10). `speed-test.result` (perf) is sent by main/speed-test-why.ts with its own attributes. ----
  'speed-test.start': {
    category: 'feature',
    from: 'main',
    fold: false,
    suffixes: ['hint', 'settings', 'support', 'card', 'other'],
    attrs: { depth: ['quick', 'performance', 'full', 'other'], pause_imports: 'boolean' },
  },
  'speed-test.refused': {
    category: 'error',
    from: 'main',
    severity: 'low',
    suffixes: speedTestBlockers,
    attrs: { blockers: 'number', samples: ['download', 'restore', 'reinstall', 'none'] },
  },
  'speed-test.samples': {
    category: 'feature',
    from: 'main',
    fold: false,
    suffixes: ['downloaded', 'restored', 'failed'],
    attrs: { ms: 'number' },
  },
  // ---- Trial to paid (§5.11). ----
  'trial.tray-shown': {
    category: 'feature',
    from: 'renderer',
    suffixes: ['n-days', 'last-day', 'ended'],
    oncePerSession: true,
    attrs: { hours_left: ['0', '1-24', '25-48', '49-72', '72+'] },
  },
  'trial.expired-gate': { category: 'feature', from: 'renderer', oncePerSession: true },
  'buy.plan': { category: 'feature', from: 'renderer', suffixes: plans },
  'checkout.start': {
    category: 'feature',
    from: 'main',
    fold: false,
    suffixes: [
      'opened',
      'offline',
      'busy',
      'rate-limited',
      'invalid-contact',
      'already-subscribed',
      'refused',
      'closed',
      'unavailable',
    ],
    attrs: { plan: plans, from: ['tray', 'license', 'expired-gate', 'link', 'other'] },
  },
  'trial.extend': {
    category: 'feature',
    from: 'main',
    suffixes: ['extended', 'already', 'refused', 'offline'],
  },
  'trial.more-time': {
    category: 'feature',
    from: 'main',
    suffixes: ['filed', 'pending', 'approved', 'denied', 'failed'],
  },
  'invoice.request': {
    category: 'feature',
    from: 'main',
    fold: false,
    suffixes: ['filed', 'already', 'failed'],
    attrs: { plan: plans },
  },
  'activation.request': {
    category: 'feature',
    from: 'main',
    suffixes: ['buy', 'questions', 'activate', 'failed'],
  },
  'partner-code.check': {
    category: 'feature',
    from: 'main',
    suffixes: ['accepted', 'not-recognized', 'failed'],
  },
  // ---- The email asks (shared/email-asks.ts). Never the email or the name: `first_name` says only whether one was given. ----
  'email-ask.opened': { category: 'feature', from: 'renderer', suffixes: emailAsks },
  'email-ask.sent': {
    category: 'feature',
    from: 'main',
    fold: false,
    suffixes: [...emailAsks, 'extra-week'],
    attrs: { tips: 'boolean', first_name: 'boolean', waiting: 'boolean' },
  },
  'email-ask.turned-off': {
    category: 'feature',
    from: 'main',
    suffixes: emailAsks,
    attrs: { via: ['app', 'email'] },
  },
  'email-ask.failed': {
    category: 'error',
    from: 'main',
    severity: 'low',
    suffixes: emailAsks,
    attrs: { reason: ['invalid-input', 'credential', 'other'] },
  },
  'app.link': {
    category: 'feature',
    from: 'main',
    suffixes: [
      'open',
      'tour',
      'settings-license',
      'settings-performance',
      'job-learning-results',
      'job-learning-train',
      'job-documents-teach',
      'job-lookups',
      'job-thresholds',
      'unknown',
    ],
  },
  // ---- Usage coverage (§5.12). ----
  'setting.changed': {
    category: 'feature',
    from: 'both',
    fold: false,
    suffixes: telemetrySettings,
    attrs: {
      kind: settingKinds,
      choice: settingChoices,
      from: ['settings', 'hint', 'setup', 'tour', 'other'],
      count: 'number',
    },
  },
  'dialog.opened': { category: 'feature', from: 'renderer', suffixes: telemetryDialogs },
  // ---- What the person saw (§5.14). ----
  'ui.error': {
    category: 'error',
    from: 'renderer',
    severity: 'med',
    suffixes: telemetryUiErrorCodes,
    attrs: { kind: ['toast', 'alert', 'status'] },
  },
} as const satisfies Record<string, TelemetryEventSpec>;

export type TelemetryEventFamily = keyof typeof telemetryEventCatalog;
type Catalog = typeof telemetryEventCatalog;
/** Every name the catalog allows: `family` or `family:<suffix>`. */
export type TelemetryEventName = {
  [F in TelemetryEventFamily]: Catalog[F] extends {
    readonly suffixes: readonly (infer S extends string)[];
  }
    ? `${F}:${S}`
    : F;
}[TelemetryEventFamily];

/**
 * The catalog's own entry for a family, never one inherited from Object.prototype: a window sends these
 * names, and "constructor" or "__proto__" looked up plainly is a spec-shaped something that passed.
 */
const catalogSpec = (family: string): TelemetryEventSpec | undefined =>
  Object.hasOwn(telemetryEventCatalog, family)
    ? (telemetryEventCatalog as Record<string, TelemetryEventSpec>)[family]
    : undefined;

/** The families a window may record (the rest only main records, where it knows the outcome). */
export const isRendererEventFamily = (family: string): boolean => {
  const spec = catalogSpec(family);
  return spec !== undefined && spec.from !== 'main';
};

export type TelemetryCatalogAttrs = Record<string, string | number | boolean>;

/**
 * A catalog event, checked: its spec, its name and the attributes it declares (numbers rounded, code words only
 * from their list). Null for anything outside the catalog.
 */
export function telemetryCatalogEvent(
  name: unknown,
  attrs: unknown,
): {
  family: TelemetryEventFamily;
  name: string;
  spec: TelemetryEventSpec;
  attrs: TelemetryCatalogAttrs;
} | null {
  if (typeof name !== 'string' || name.length > 120) return null;
  const colon = name.indexOf(':');
  const family = colon < 0 ? name : name.slice(0, colon);
  const suffix = colon < 0 ? null : name.slice(colon + 1);
  const spec = catalogSpec(family);
  if (!spec) return null;
  if (spec.suffixes ? suffix === null || !spec.suffixes.includes(suffix) : suffix !== null)
    return null;
  const clean: TelemetryCatalogAttrs = {};
  if (attrs && typeof attrs === 'object') {
    for (const [key, value] of Object.entries(attrs as Record<string, unknown>)) {
      // Own declared attributes only: an inherited "toString" is a function, and asking it `includes`
      // threw inside main's telemetry handler.
      const want = spec.attrs && Object.hasOwn(spec.attrs, key) ? spec.attrs[key] : undefined;
      if (want === undefined) continue;
      if (want === 'number') {
        if (typeof value === 'number' && Number.isFinite(value))
          clean[key] = Math.round(value * 100) / 100;
      } else if (want === 'boolean') {
        if (typeof value === 'boolean') clean[key] = value;
      } else if (typeof value === 'string' && want.includes(value)) clean[key] = value;
    }
  }
  return { family: family as TelemetryEventFamily, name, spec, attrs: clean };
}

/** A count as a bucket for the `files`/`documents`/`pages` attributes: never the exact number of someone's files. */
export function telemetryCountBucket(count: number): (typeof fileBuckets)[number] {
  return count <= 0
    ? '0'
    : count === 1
      ? '1'
      : count <= 10
        ? '2-10'
        : count <= 100
          ? '11-100'
          : '100+';
}
